import { Box, Button, ButtonGroup, CardMedia, Dialog, DialogActions, DialogContent, Grid, TextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import FileDrop from 'components/file-drop/FileDrop'
import { HeaderBlock } from 'components/header-block/HeaderBlock'
import LayoutCard from 'components/layout-card/LayoutCard'
import PageContainer from 'components/page-container/PageContainer'
import Textbox from 'components/textbox/Textbox'
import { DeleteModal } from 'containers/delete-modal/DeleteModal'
import FileSaver from 'file-saver'
import { getIcon } from 'icon/icon'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { RouteComponentProps } from 'react-router'
import { deleteFiles, downloadFileBlob, getCurrentContent, getImageFromUrl, uploadFile } from 'state/actions/FileDownloadActions'
import { RootState } from 'state/reducers/AppReducer'
import { FixMeLater } from 'types/FixMeLaterType'

interface MatchParams {
    contentId: string
}

interface ViewContentPageProps extends RouteComponentProps<MatchParams> {
}

interface CurrentContent {
    DisplayName: string,
    FileExtension?: string,
    Active: boolean,
    Type: string,
    Path: string,
    id: string,
    Tags: ContentTags[]
}

interface ContentTags {
    TagName: string
}

const useStyes = makeStyles({
    container: {
        margin: "30px"
    },
    image: {
        objectFit: "contain",
        margin: "8px"
    },
})

function ViewContentPage(props: ViewContentPageProps) {

    const currentContent = useSelector<RootState, FixMeLater>(state => state.FileDownloadReducer.currentContent)

    const [t, i18n] = useTranslation()
    const classes = useStyes()
    const dispatch = useDispatch()

    const [image, setImage] = useState<string | null>(null);
    const [openMediaModal, setOpenMediaModal] = useState(false)
    const [editing, setEditing] = useState(false)
    const [file, setFile] = useState<File[] | null | undefined>(null)
    const [content, setContent] = useState<CurrentContent | undefined | null>(null)
    const [fetchImage, setFetchImage] = useState(false)

    useEffect(() => {
        dispatch(getCurrentContent(props.match.params.contentId, () => {
            setFetchImage(true)
        }))
    }, [])

    useEffect(() => {
        if (currentContent != null) {
            setContent(currentContent)
        }
    }, [currentContent])

    useEffect(() => {
        if (fetchImage && content?.id != null) {
            dispatch(downloadFileBlob(currentContent.id, (image: string) => {
                setImage(image)
                setFetchImage(false)
            }))
        }
    }, [fetchImage])

    const setOpenMediaContentModalTrue = () => setOpenMediaModal(true)

    function clearChanges() {
        setOpenMediaModal(false)
        setEditing(false)
    }

    function handleUpdateContent() {
        if (editing) {
            const fileToUpload = file != null ? file[0] : null
            dispatch(uploadFile(content, fileToUpload, () => {
                setFetchImage(true)
            }))
            clearChanges()
        } else {
            setEditing(true)
        }
    }

    function handleDownload() {
        if (image != null) {
            FileSaver.saveAs(image, currentContent.DisplayName);
        }
    }

    function updateContentFileName(value: string) {
        const newContent = Object.assign({}, content)
        newContent.DisplayName = value
        setContent(newContent)
    }

    function updateContentTags(value: string[]) {
        const newContent = Object.assign({}, content)
        newContent.Tags = !!value ? value.map(item => ({ TagName: item })) : []
        setContent(newContent)
    }

    function updateContentFileType(value: string) {
        const newContent = Object.assign({}, content)
        newContent.Type = value
        setContent(newContent)
    }

    function handleDeleteContent(content: string[]) {
        dispatch(deleteFiles(content, (success: boolean) => {
            if (success) {
                props.history.goBack()
            }
        }))
    }

    function handleCancelContent() {
        clearChanges()
    }

    function renderContent() {
        return (
            <Box className={classes.container}>
                <Grid container spacing={2}>
                    <Grid item sm={12} md={6}>
                        <LayoutCard
                            title={t('content.metadata')}
                            cardHeight="400px"
                            layout={[
                                {
                                    label: t('content.id'),
                                    handleChange: () => { },
                                    type: TextField,
                                    value: content?.id,
                                    disabled: true,
                                    grid: 12
                                },
                                {
                                    label: t('content.file-name'),
                                    handleChange: updateContentFileName,
                                    type: TextField,
                                    value: content?.DisplayName,
                                    disabled: !editing,
                                    grid: 12
                                },
                                {
                                    label: t('content.file-type'),
                                    handleChange: updateContentFileType,
                                    type: TextField,
                                    value: content?.Type,
                                    disabled: !editing,
                                    grid: 12
                                },
                                {
                                    label: t('content.tags'),
                                    handleChange: updateContentTags,
                                    type: Textbox,
                                    value: !!content?.Tags ? content.Tags.map(tag => tag.TagName) : [],
                                    disabled: !editing,
                                    data: [

                                    ],
                                    grid: 12
                                }
                            ]}
                        />
                    </Grid>
                    <Grid item sm={12} md={6}>

                        <LayoutCard
                            title={t('content.media')}
                            cardHeight="400px"
                            right={
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={handleDownload}
                                >
                                    {t('common.download')}
                                </Button>
                            }
                            layout={editing ?
                                [
                                    {
                                        label: "fileDrop",
                                        handleChange: (file) => setFile(file),
                                        type: FileDrop,
                                        value: file,
                                        message: t('content.upload'),
                                        grid: 12,
                                    }
                                ] :
                                [
                                    {
                                        label: "image",
                                        handleChange: () => { },
                                        type: CardMedia,
                                        value: image || "",
                                        alt: content?.DisplayName || t('content.unknown-filename'),
                                        height: 255,
                                        grid: 12
                                    }
                                ]
                            }
                        />
                    </Grid>
                </Grid>
            </Box>
        )
    }

    function renderDeleteContentModal() {
        return (
            <DeleteModal
                open={openMediaModal}
                data={[{ id: content?.id, Name: content?.DisplayName }]}
                handleDone={handleDeleteContent}
                handleCancel={handleCancelContent}
            />
        )
    }

    return (
        <Box>
            <HeaderBlock
                title={currentContent?.DisplayName}
                right={
                    <Grid container>
                        <Grid item>
                            <ButtonGroup>
                                <Button
                                    disabled={!editing}
                                    variant="outlined"
                                    color="primary"
                                    onClick={setOpenMediaContentModalTrue}
                                >
                                    {t('common.delete')}
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={handleUpdateContent}
                                >
                                    {editing ? t('common.save') : t('common.edit')}
                                </Button>
                            </ButtonGroup>
                        </Grid>
                    </Grid>
                }
            />

            {renderDeleteContentModal()}

            <PageContainer>
                {renderContent()}
            </PageContainer>
        </Box>
    )

}

export default ViewContentPage